import type { ChannelSummaryDto, ConversationDto } from '@dijitalapp/shared';
import { useEffect, useState, type FormEvent } from 'react';
import type { MessageKey } from '@dijitalapp/i18n';
import { NavLink, useNavigate, useParams } from 'react-router';
import { PageEmpty, PageHeader } from '../components/SectionNav';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { useChat, useChatStore } from '../lib/chat-context';
import { conversationTitle } from './Messages';
import { useShell } from './Shell';

/** `/discover`: the user's channels, the public channel directory (adults only) and creating a channel. */
export function ChannelsPage() {
  const { i18n, me } = useApp();
  const { t } = i18n;
  const { capabilities, setMobileView } = useShell();
  // /discover/mine (default), /discover/browse, /discover/new: the list of sections is in the middle pane.
  const { section: param } = useParams();
  const section = param === 'browse' || param === 'new' ? param : 'mine';
  const chat = useChat();
  const store = useChatStore();
  const navigate = useNavigate();
  const adult = me?.ageBand === 'adult';
  const canPublic = Boolean(capabilities?.canBroadcastPublicly);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<ChannelSummaryDto[] | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mine = (chat?.conversations ?? []).filter((c) => c.type === 'channel');

  useEffect(() => {
    if (!canPublic) return;
    let alive = true;
    // A short pause so the directory is not queried on every keystroke.
    const timer = setTimeout(() => {
      api<{ channels: ChannelSummaryDto[] }>('GET', `/channels/discover${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''}`).then(
        (r) => alive && setResults(r.channels),
        (err: unknown) => alive && setError(t(errorMessageKey(err))),
      );
    }, 250);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [q, canPublic, t]);

  const open = async (id: string) => {
    await store?.loadConversations();
    navigate(`/messages/${id}`);
  };

  const subscribe = async (id: string) => {
    setError(null);
    try {
      await api<ConversationDto>('POST', `/channels/${id}/subscribe`);
      await open(id);
    } catch (err) {
      setError(t(errorMessageKey(err)));
    }
  };

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const c = await api<ConversationDto>('POST', '/channels', { title, description: description || undefined, public: isPublic });
      setTitle('');
      setDescription('');
      await open(c.id);
    } catch (err) {
      setError(t(errorMessageKey(err)));
    }
  };

  return (
    <div className="content-inner">
      <PageHeader
        title={t(section === 'browse' ? 'channel.directory' : section === 'new' ? 'channel.create' : 'channel.mine')}
        description={t('channel.intro')}
        back={() => setMobileView('list')}
      />
      {error && (
        <div className="alert alert-error" role="alert">
          {error}
        </div>
      )}

      {section === 'mine' &&
        (mine.length === 0 ? (
          <PageEmpty icon="discover" title={t('channel.mine')} body={t('channel.none')} action={<NavLink className="btn" to="/discover/browse">{t('channel.directory')}</NavLink>} />
        ) : (
          <>
      <section className="card stack" aria-labelledby="my-channels">
        <h2 id="my-channels" className="visually-hidden">{t('channel.mine')}</h2>
        {mine.length === 0 && <p className="muted">{t('channel.none')}</p>}
        {mine.map((c) => (
          <NavLink key={c.id} to={`/messages/${c.id}`} className="list-item">
            <span style={{ minWidth: 0 }}>
              <strong className="truncate">{conversationTitle(c)}</strong>
              <br />
              <span className="muted">{t('channel.subscribers', { count: c.group?.memberCount ?? 0 })}</span>
            </span>
            <span className="spacer" />
            {(c.group?.myRole === 'owner' || c.group?.myRole === 'admin') && <span className="badge">{t(`group.role.${c.group.myRole}` as MessageKey)}</span>}
          </NavLink>
        ))}
      </section>

          </>
        ))}
      {section === 'browse' && (
        <>
      {canPublic ? (
        <section className="card stack" aria-labelledby="channel-directory">
          <h2 id="channel-directory" className="visually-hidden">{t('channel.directory')}</h2>
          <label className="visually-hidden" htmlFor="channel-search">
            {t('channel.search')}
          </label>
          <input
            id="channel-search"
            className="input"
            type="search"
            maxLength={80}
            placeholder={t('channel.search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {results === null && <p className="muted">{t('app.loading')}</p>}
          {results?.length === 0 && <p className="muted">{t('channel.noResults')}</p>}
          {results?.map((c) => (
            <div key={c.id} className="list-item">
              <span style={{ minWidth: 0 }}>
                <strong className="truncate">📢 {c.title}</strong>
                <br />
                <span className="muted">
                  {c.description ? `${c.description} · ` : ''}
                  {t('channel.subscribers', { count: c.subscriberCount })}
                </span>
              </span>
              <span className="spacer" />
              {c.subscribed ? (
                <button className="btn" type="button" onClick={() => void open(c.id)}>
                  {t('join.open')}
                </button>
              ) : (
                <button className="btn btn-primary" type="button" onClick={() => void subscribe(c.id)}>
                  {t('channel.subscribe')}
                </button>
              )}
            </div>
          ))}
          <p className="hint">{t('channel.privacyNote')}</p>
        </section>
      ) : (
        <p className="hint">{t('channel.adultsOnly')}</p>
      )}

        </>
      )}
      {section === 'new' && (
        <>
      {adult && (
        <form className="card stack" onSubmit={create}>
          <div className="field">
            <label htmlFor="channel-title">{t('channel.name')}</label>
            <input id="channel-title" className="input" required maxLength={80} value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="channel-description">{t('group.description')}</label>
            <input
              id="channel-description"
              className="input"
              maxLength={500}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          {canPublic && (
            <label className="row">
              <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
              <span>{t('channel.public')}</span>
            </label>
          )}
          <p className="hint">{isPublic ? t('channel.publicNote') : t('channel.privateNote')}</p>
          <div className="row">
            <span className="spacer" />
            <button className="btn btn-primary">{t('channel.create')}</button>
          </div>
        </form>
      )}
        </>
      )}
    </div>
  );
}
